import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  IntegrationConnectionStatus,
  IntegrationProviderId,
  type IntegrationConnection,
  type CatalogProperty,
  type PrepareMeliPublicationDraftParams,
} from 'src/api/generated';
import { useAlert } from 'src/contexts/alert-context';
import { useRepairPropertySync } from 'src/modules/shared/hooks/api/catalog/properties.mutation';
import { useIntegrations } from 'src/modules/shared/hooks/api/integrations/integrations';
import { useEnqueueMeliPublish } from 'src/modules/shared/hooks/api/integrations/meli-publications';
import {
  useEnqueuePortalPublicationUnpublish,
  usePortalPublications,
} from 'src/modules/shared/hooks/api/integrations/portal-publications';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { getMeliIntegrationMode } from 'src/modules/shared/utils/integrations/meli-integration.util';
import {
  getCurrentMeliPublication,
  getSupportedMeliOperations,
  shouldContinueMeliPublicationPolling,
  type SupportedMeliOperation,
} from '../utils/meli-publication.util';
import { POLLING_INTERVAL_MS } from '../utils/property-publishing-panel.util';
import type { QueuedPublicationAction } from '../types/property-publishing-panel.type';
import { resolvePropertySourceProviderId } from '../utils/property-source-integration.util';

interface UsePropertyPublishingStateParams {
  property: CatalogProperty | null;
}

interface PropertyPublishingConnectionState {
  /** Whether the tenant currently has an active MeLi connection. */
  isMeliConnected: boolean;
  /** Whether the tenant has any MeLi connection configured. */
  hasMeliConnection: boolean;
  /** Whether the current property originated from a supported synced integration. */
  isSourceIntegrationProperty: boolean;
  /** Whether the current property's source integration is active and sync-managed. */
  isSourceIntegrationSyncManaged: boolean;
  /** Whether the current property's source integration is active. */
  isSourceIntegrationConnected: boolean;
  /** Whether the current property originated from MeLi sync. */
  isMeliSourcedProperty: boolean;
  /** Whether the active MeLi connection is running in sync mode. */
  isMeliSyncMode: boolean;
  /** Whether the integrations query is still loading. */
  loadingIntegrations: boolean;
  /** Source integration provider id resolved from the property source system. */
  sourceProviderId: ReturnType<typeof resolvePropertySourceProviderId>;
  /** Raw MeLi connection metadata when available. */
  meliConnection: IntegrationConnection | null;
  /** Health status of the source integration when available. */
  sourceIntegrationHealthStatus: IntegrationConnection['healthStatus'] | null;
}

interface PropertyPublishingModalState {
  /** Operation currently opened in the MeLi management modal. */
  managerOperation: SupportedMeliOperation | null;
  /** Publication currently opened in the MeLi management modal. */
  managerPublicationId: number | null;
  /** Updates the operation currently managed in the modal. */
  setManagerOperation: Dispatch<SetStateAction<SupportedMeliOperation | null>>;
  /** Updates the publication currently managed in the modal. */
  setManagerPublicationId: Dispatch<SetStateAction<number | null>>;
}

interface PropertyPublishingQueryState {
  /** Supported MeLi operations for the current property. */
  operations: SupportedMeliOperation[];
  /** Persisted MeLi publications already stored for the property. */
  publications: ReturnType<typeof usePortalPublications>['publications'];
  /** Refetch helper for publication state updates. */
  refetchPublications: ReturnType<typeof usePortalPublications>['refetch'];
}

interface PropertyPublishingQueuedState {
  /** Queued publish or unpublish action currently awaiting completion. */
  action: QueuedPublicationAction | null;
  /** Allows modal flows to clear or replace the queued action. */
  setQueuedAction: Dispatch<SetStateAction<QueuedPublicationAction | null>>;
}

interface PropertyPublishingShortcutActions {
  /** Whether a publish shortcut request is currently running. */
  enqueueingPublish: boolean;
  /** Whether an unpublish shortcut request is currently running. */
  enqueueingUnpublish: boolean;
  /** Enqueues a publish action directly from the publication rail. */
  handlePublishShortcut: (
    operation: SupportedMeliOperation,
    params: PrepareMeliPublicationDraftParams | null
  ) => Promise<void>;
  /** Enqueues an unpublish action directly from the publication rail. */
  handleUnpublishShortcut: (
    operation: SupportedMeliOperation,
    publicationId: number
  ) => Promise<void>;
}

interface PropertyPublishingRepairSyncState {
  /** Whether a property-scoped repair sync request is currently being enqueued. */
  loading: boolean;
  /** Enqueues a property-scoped repair sync when the current property is eligible. */
  handleRepairSync: () => Promise<void>;
}

interface UsePropertyPublishingStateResult {
  /** Connection and source-of-truth information for the current property. */
  connection: PropertyPublishingConnectionState;
  /** Whether the persisted publication list is still loading. */
  loading: boolean;
  /** Modal selection state shared with the publishing manager. */
  modal: PropertyPublishingModalState;
  /** Persisted publications and supported operations for the current property. */
  publications: PropertyPublishingQueryState;
  /** Queued publication action state used for optimistic row feedback. */
  queued: PropertyPublishingQueuedState;
  /** Direct row actions exposed by the publishing rail. */
  shortcuts: PropertyPublishingShortcutActions;
  /** Property-scoped repair-sync action state for sync-managed source properties. */
  repairSync: PropertyPublishingRepairSyncState;
}

/**
 * Connection state, publication query, queued-action lifecycle, and inline
 * publish/unpublish actions for the property publishing rail.
 */
export const usePropertyPublishingState = ({
  property,
}: UsePropertyPublishingStateParams): UsePropertyPublishingStateResult => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const { integrations, loading: loadingIntegrations } = useIntegrations();
  const [queuedAction, setQueuedAction] =
    useState<QueuedPublicationAction | null>(null);
  const [managerOperation, setManagerOperation] =
    useState<SupportedMeliOperation | null>(null);
  const [managerPublicationId, setManagerPublicationId] = useState<
    number | null
  >(null);

  const meliConnection = useMemo(
    () =>
      integrations?.items.find(
        (item) => item.providerId === IntegrationProviderId.MELI
      )?.connection ?? null,
    [integrations]
  );
  const isMeliConnected =
    meliConnection?.status === IntegrationConnectionStatus.ACTIVE;
  const hasMeliConnection = meliConnection != null;
  const isMeliSyncMode =
    isMeliConnected &&
    getMeliIntegrationMode(meliConnection.config ?? null) === 'SYNC';
  const isMeliSourcedProperty = property?.sourceSystem === 'meli';
  const sourceProviderId = resolvePropertySourceProviderId(
    property?.sourceSystem
  );
  const sourceConnection =
    sourceProviderId != null
      ? integrations?.items.find((item) => item.providerId === sourceProviderId)
          ?.connection ?? null
      : null;
  const isSourceIntegrationProperty = sourceProviderId != null;
  const isSourceIntegrationConnected =
    sourceConnection?.status === IntegrationConnectionStatus.ACTIVE;
  const isSourceIntegrationSyncManaged =
    isSourceIntegrationConnected &&
    (sourceProviderId === IntegrationProviderId.MELI
      ? getMeliIntegrationMode(sourceConnection.config ?? null) === 'SYNC'
      : true);
  const sourceIntegrationHealthStatus = sourceConnection?.healthStatus ?? null;

  const filterBy = useMemo(() => {
    if (!property) {
      return undefined;
    }

    return createQuery()
      .equals('propertyId', property.id)
      .equals('portal', 'meli')
      .take(20)
      .build().filterBy;
  }, [property]);

  const {
    publications,
    loading,
    refetch: refetchPublications,
  } = usePortalPublications({
    filterBy,
    enabled: property != null,
    refetchInterval: queuedAction ? POLLING_INTERVAL_MS : false,
  });

  const operations = useMemo(
    () => (property ? getSupportedMeliOperations(property) : []),
    [property]
  );

  useEffect(() => {
    if (!managerOperation) {
      if (managerPublicationId != null) {
        setManagerPublicationId(null);
      }
      return;
    }

    if (!operations.includes(managerOperation)) {
      setManagerOperation(operations[0] ?? null);
      setManagerPublicationId(null);
    }
  }, [managerOperation, managerPublicationId, operations]);

  const queuedPublication = useMemo(() => {
    if (!queuedAction) {
      return null;
    }

    return getCurrentMeliPublication(publications, queuedAction.operation);
  }, [publications, queuedAction]);

  useEffect(() => {
    if (
      queuedAction &&
      !shouldContinueMeliPublicationPolling(
        queuedPublication,
        queuedAction.action
      )
    ) {
      setQueuedAction(null);
    }
  }, [queuedAction, queuedPublication]);

  const { enqueuePublish, loading: enqueueingPublish } =
    useEnqueueMeliPublish();
  const {
    enqueuePersistedAction: enqueueUnpublish,
    loading: enqueueingUnpublish,
  } = useEnqueuePortalPublicationUnpublish();
  const { repairSyncProperty, loading: enqueueingRepairSync } =
    useRepairPropertySync();

  const handlePublishShortcut = useCallback(
    async (
      operation: SupportedMeliOperation,
      params: Parameters<typeof enqueuePublish>[0] | null
    ) => {
      if (!params) {
        return;
      }

      try {
        await enqueuePublish(params);
        setQueuedAction({ action: 'publish', operation });
        showAlert(t('catalog.properties.meli.alert.publishQueued'), 'success');
        await refetchPublications();
      } catch (error) {
        showAlert(extractApiErrorMessage(error), 'error');
      }
    },
    [enqueuePublish, refetchPublications, showAlert, t]
  );

  const handleUnpublishShortcut = useCallback(
    async (operation: SupportedMeliOperation, publicationId: number) => {
      try {
        await enqueueUnpublish(publicationId);
        setQueuedAction({ action: 'unpublish', operation });
        showAlert(
          t('catalog.properties.meli.alert.unpublishQueued'),
          'success'
        );
        await refetchPublications();
      } catch (error) {
        showAlert(extractApiErrorMessage(error), 'error');
      }
    },
    [enqueueUnpublish, refetchPublications, showAlert, t]
  );

  const handleRepairSync = useCallback(async () => {
    if (!property) {
      return;
    }

    try {
      await repairSyncProperty(property.id);
      showAlert(
        t('catalog.properties.editor.publishing.alert.repairSyncQueued'),
        'success'
      );
      await refetchPublications();
    } catch (error) {
      showAlert(extractApiErrorMessage(error), 'error');
    }
  }, [property, refetchPublications, repairSyncProperty, showAlert, t]);

  return {
    connection: {
      hasMeliConnection,
      isMeliConnected,
      isSourceIntegrationProperty,
      isSourceIntegrationConnected,
      isSourceIntegrationSyncManaged,
      isMeliSourcedProperty,
      isMeliSyncMode,
      loadingIntegrations,
      sourceProviderId,
      meliConnection,
      sourceIntegrationHealthStatus,
    },
    loading,
    modal: {
      managerOperation,
      managerPublicationId,
      setManagerOperation,
      setManagerPublicationId,
    },
    publications: {
      operations,
      publications,
      refetchPublications,
    },
    queued: {
      action: queuedAction,
      setQueuedAction,
    },
    shortcuts: {
      enqueueingPublish,
      enqueueingUnpublish,
      handlePublishShortcut,
      handleUnpublishShortcut,
    },
    repairSync: {
      loading: enqueueingRepairSync,
      handleRepairSync,
    },
  };
};
